import type { FC, PropsWithChildren } from "react";
import { useCallback, useEffect, useState } from "react";
import { Button, Card } from "@ptsd/uikit";
import { useToastController } from "@tamagui/toast";
import { useTranslation } from "react-i18next";
import { Text, XStack, YStack } from "tamagui";

import type { RouterOutputs } from "~/utils/api";
import { api } from "~/utils/api";

type Research = RouterOutputs["personal"]["types"][number];
export interface PersonResearchProps {
  personId: string;
  research: Research;
}

export const PersonResearch: FC<PropsWithChildren<PersonResearchProps>> = ({
  personId,
  research,
  children,
}) => {
  const { t } = useTranslation("person");
  const toast = useToastController();

  const [disabled, setDisabled] = useState(!research.creatable);
  useEffect(() => setDisabled(!research.creatable), [research.creatable]);

  const { mutate } = api.personal.createNewSurvey.useMutation({
    onSuccess: () => {
      setDisabled(true);
      toast.show(t("researchNewSuccess"), { variant: "success" });
    },
    onError: () => toast.show(t("researchNewError"), { variant: "error" }),
  });

  const handlePressNew = useCallback(() => {
    if (research.type === "onboarding") return;
    mutate({ type: research.type, personId });
  }, [mutate, research, personId]);

  return (
    <YStack
      gap={"$0"}
      borderBottomColor={"$gray-200"}
      borderBottomWidth={"$0.1"}
      borderRadius={"$7"}
      overflow={"hidden"}
      borderWidth={"$0.2"}
      borderColor={"$gray-200"}
    >
      <Card
        variant="borderless"
        paddingHorizontal={"$3"}
        paddingVertical={"$2"}
        rounded="none"
        backgroundColor={"$warm-snow-500"}
        borderBottomColor={"$gray-200"}
      >
        <XStack gap={"$3"} justifyContent="space-between" alignItems="center">
          <XStack gap={"$1"}>
            <Text fontFamily={"$heading"} fontWeight={"$6"} fontSize={"$6"}>
              {t("research")}
            </Text>
            <Text fontFamily={"$heading"} fontWeight={"$5"} fontSize={"$6"}>
              {research.name}
            </Text>
          </XStack>
          <Button
            variant="primary"
            size="sm"
            rounded="md"
            disabled={disabled}
            onPress={handlePressNew}
          >
            {t("researchNew")}
          </Button>
        </XStack>
      </Card>
      {children}
    </YStack>
  );
};
